import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['rows', 'template']

  connect() {
    this.updateAllRows()
  }

  updateAllRows() {
    if (!this.hasRowsTarget) return
    this.rowsTarget
      .querySelectorAll('[data-premium-adjustment-conditions-target="row"]')
      .forEach((row) => {
        this.updateRow(row)
        this.updateTargetValueInputs(row)
      })
  }

  changeSourceType(event) {
    const row = event.currentTarget.closest('[data-premium-adjustment-conditions-target="row"]')
    if (!row) return
    this.clearFieldKeyValues(row)
    this.updateRow(row)
  }

  changeValueType(event) {
    const row = event.currentTarget.closest('[data-premium-adjustment-conditions-target="row"]')
    if (!row) return
    this.clearTargetValueInputs(row)
    this.updateTargetValueInputs(row)
  }

  changeComparisonOperator(event) {
    const row = event.currentTarget.closest('[data-premium-adjustment-conditions-target="row"]')
    if (!row) return
    const previousValue = this.activeTargetValueElement(row)?.value
    this.updateTargetValueInputs(row)
    const nextActive = this.activeTargetValueElement(row)
    if (nextActive && previousValue !== undefined && previousValue !== null) {
      nextActive.value = previousValue
    }
  }

  activeTargetValueElement(row) {
    const targets = [
      'targetValueString',
      'targetValueInteger',
      'targetValueDecimal',
      'targetValueBoolean',
    ]
    for (const target of targets) {
      const el = row.querySelector(`[data-premium-adjustment-conditions-target="${target}"]`)
      if (el && !el.disabled && !el.classList.contains('hidden')) return el
    }
    return null
  }

  clearFieldKeyValues(row) {
    const fieldKeyTargets = ['fieldKeyAuiSelect', 'fieldKeyQuoteSelect', 'fieldKeyDerivedSelect']
    fieldKeyTargets.forEach((target) => {
      const el = row.querySelector(`[data-premium-adjustment-conditions-target="${target}"]`)
      if (el) el.value = ''
    })
  }

  clearTargetValueInputs(row) {
    const targets = [
      'targetValueString',
      'targetValueInteger',
      'targetValueDecimal',
      'targetValueBoolean',
    ]
    targets.forEach((target) => {
      const el = row.querySelector(`[data-premium-adjustment-conditions-target="${target}"]`)
      if (el) el.value = ''
    })
  }

  updateRow(row) {
    const sourceTypeSelect = row.querySelector('[data-premium-adjustment-conditions-target="sourceType"]')
    const fieldKeyAuiSelect = row.querySelector('[data-premium-adjustment-conditions-target="fieldKeyAuiSelect"]')
    const fieldKeyQuoteSelect = row.querySelector('[data-premium-adjustment-conditions-target="fieldKeyQuoteSelect"]')
    const fieldKeyDerivedSelect = row.querySelector('[data-premium-adjustment-conditions-target="fieldKeyDerivedSelect"]')

    if (!sourceTypeSelect || !fieldKeyAuiSelect || !fieldKeyQuoteSelect || !fieldKeyDerivedSelect) return

    const hideAllFieldKeyControls = () => {
      [fieldKeyAuiSelect, fieldKeyQuoteSelect, fieldKeyDerivedSelect].forEach((el) => {
        el.classList.add('hidden')
        el.setAttribute('disabled', 'disabled')
      })
    }

    const showAui = () => {
      hideAllFieldKeyControls()
      fieldKeyAuiSelect.classList.remove('hidden')
      fieldKeyAuiSelect.removeAttribute('disabled')
    }

    const showQuoteAttribute = () => {
      hideAllFieldKeyControls()
      fieldKeyQuoteSelect.classList.remove('hidden')
      fieldKeyQuoteSelect.removeAttribute('disabled')
    }

    const showDerivedMetric = () => {
      hideAllFieldKeyControls()
      fieldKeyDerivedSelect.classList.remove('hidden')
      fieldKeyDerivedSelect.removeAttribute('disabled')
    }

    if (sourceTypeSelect.value === 'aui') {
      showAui()
    } else if (sourceTypeSelect.value === 'quote_attribute') {
      showQuoteAttribute()
    } else if (sourceTypeSelect.value === 'derived_metric') {
      showDerivedMetric()
    } else {
      hideAllFieldKeyControls()
    }
  }

  updateTargetValueInputs(row) {
    const comparisonSelect = row.querySelector('[data-premium-adjustment-conditions-target="comparisonOperator"]')
    const valueTypeSelect = row.querySelector('[data-premium-adjustment-conditions-target="valueType"]')
    const stringEl = row.querySelector('[data-premium-adjustment-conditions-target="targetValueString"]')
    const integerEl = row.querySelector('[data-premium-adjustment-conditions-target="targetValueInteger"]')
    const decimalEl = row.querySelector('[data-premium-adjustment-conditions-target="targetValueDecimal"]')
    const booleanEl = row.querySelector('[data-premium-adjustment-conditions-target="targetValueBoolean"]')

    if (!comparisonSelect || !valueTypeSelect || !stringEl || !integerEl || !decimalEl || !booleanEl) return

    const hideAllTargetValues = () => {
      [stringEl, integerEl, decimalEl, booleanEl].forEach((el) => {
        el.classList.add('hidden')
        el.setAttribute('disabled', 'disabled')
      })
    }

    const operator = comparisonSelect.value
    if (operator === 'in' || operator === 'not_in') {
      hideAllTargetValues()
      stringEl.classList.remove('hidden')
      stringEl.removeAttribute('disabled')
      return
    }

    const vt = valueTypeSelect.value
    hideAllTargetValues()

    if (vt === 'boolean') {
      booleanEl.classList.remove('hidden')
      booleanEl.removeAttribute('disabled')
    } else if (vt === 'integer') {
      integerEl.classList.remove('hidden')
      integerEl.removeAttribute('disabled')
    } else if (vt === 'decimal') {
      decimalEl.classList.remove('hidden')
      decimalEl.removeAttribute('disabled')
    } else {
      stringEl.classList.remove('hidden')
      stringEl.removeAttribute('disabled')
    }
  }

  addCondition() {
    if (!this.hasTemplateTarget || !this.hasRowsTarget) return

    const newId = new Date().getTime().toString()
    // <template> contents live in .content (DocumentFragment), not as child nodes, so
    // reading innerHTML from the <template> element is often empty across browsers.
    const holder = document.createElement('div')
    holder.appendChild(this.templateTarget.content.cloneNode(true))
    const content = holder.innerHTML.replace(/NEW_CONDITION/g, newId)

    this.rowsTarget.insertAdjacentHTML('beforeend', content)

    const rows = this.rowsTarget.querySelectorAll('[data-premium-adjustment-conditions-target="row"]')
    const newRow = rows[rows.length - 1]
    if (newRow) {
      this.updateRow(newRow)
      this.updateTargetValueInputs(newRow)
    }
  }
}
